import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useRequireAuth, useAuth } from '../../utils/auth'
import { platformApi } from '../../utils/api'
import { ThemeToggle } from '../../components/ThemeContext'
import { getPlatformConfig } from '../../utils/config'

interface Stats {
  totalTenants: number
  totalRegistrations: number
  monthlyRevenue: number
  activeCamps: number
}

export default function PlatformDashboard() {
  const navigate = useNavigate()
  const config = getPlatformConfig()
  const { session, loading, authorized } = useRequireAuth('platform')
  const { logout } = useAuth()
  const [stats, setStats] = useState<Stats | null>(null)
  const [loadingStats, setLoadingStats] = useState(true)

  useEffect(() => {
    if (!loading && !authorized) {
      navigate('/platform')
    }
  }, [loading, authorized, navigate])

  useEffect(() => {
    if (authorized) {
      loadStats()
    }
  }, [authorized])

  const loadStats = async () => {
    try {
      const data = await platformApi.getStats() as { stats: Stats }
      setStats(data.stats)
    } catch (err) {
      console.error('Failed to load stats:', err)
    } finally {
      setLoadingStats(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
      </div>
    )
  }

  if (!authorized) return null

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21h18M5 21V7l7-4 7 4v14" />
              </svg>
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Platform Admin</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">{session?.email}</p>
            </div>
          </div>
          
          <nav className="flex items-center gap-4">
            <Link to="/platform/dashboard" className="text-gray-900 dark:text-white font-medium">Dashboard</Link>
            <Link to="/platform/tenants" className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition">Tenants</Link>
            <ThemeToggle />
            <button onClick={logout} className="btn-ghost text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
              Sign Out
            </button>
          </nav>
        </div>
      </header>
      
      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h2>
          <Link to="/platform/tenants/new" className="btn-primary">
            + New Tenant
          </Link>
        </div>
        
        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {[
            { label: 'Total Tenants', value: stats?.totalTenants ?? '-', icon: '🏢' },
            { label: 'Active Camps', value: stats?.activeCamps ?? '-', icon: '🏕️' },
            { label: 'Registrations', value: stats?.totalRegistrations ?? '-', icon: '📋' },
            { label: 'Monthly Revenue', value: stats?.monthlyRevenue ? `$${stats.monthlyRevenue.toLocaleString()}` : '-', icon: '💰' },
          ].map((stat, i) => (
            <div key={i} className="card p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-500 dark:text-gray-400 text-sm">{stat.label}</span>
                <span className="text-2xl">{stat.icon}</span>
              </div>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {loadingStats ? (
                  <span className="inline-block w-16 h-8 bg-gray-200 dark:bg-gray-800 rounded animate-pulse" />
                ) : stat.value}
              </p>
            </div>
          ))}
        </div>
        
        {/* Quick Actions */}
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link to="/platform/tenants/new" className="card p-6 hover:border-brand-500/50 transition-colors group">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-brand-500/10 flex items-center justify-center text-2xl group-hover:bg-brand-500/20 transition">
                ➕
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Create Tenant</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">Add a new organization</p>
              </div>
            </div>
          </Link>
          
          <Link to="/platform/tenants" className="card p-6 hover:border-brand-500/50 transition-colors group">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-brand-500/10 flex items-center justify-center text-2xl group-hover:bg-brand-500/20 transition">
                📊
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">View Tenants</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">Manage all organizations</p>
              </div>
            </div>
          </Link>
          
          <div className="card p-6 opacity-50 cursor-not-allowed">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-gray-200 dark:bg-gray-800 flex items-center justify-center text-2xl">
                ⚙️
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Settings</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">Coming soon</p>
              </div>
            </div>
          </div>
        </div>
        
        <p className="text-sm text-gray-400 dark:text-gray-500 mt-8">
          Platform domain: {config.platformDomain}
        </p>
      </main>
    </div>
  )
}
